import React, { useEffect, useMemo, useState } from "react";

import moment from "moment";

import {
  calculateFieldContributionStatus,
  checkFeatureFlag,
  getEnvironment,
  isEmptyObject,
  isSingleFieldOutcomes,
  someOutcomeHasErrorOfType,
  useGetCarbonProgramByFarmId,
  useGetFarmPayments,
  useGetFarmReportingYear,
  useGetFarms,
  useGetFieldEvaluationMetricCorrections,
  useTranslation,
} from "utils";

import {
  CARBON_RUNNER_ERROR_TYPE,
  CarbonProgramOutcomesResponse,
  EVALUATION_METRICS,
  IMPACT_PANEL_CALCULATION_TYPE,
  IMPACT_PANEL_RESULT_TYPE,
  PRACTICE_SLUG,
  Payment,
  PracticeBundle,
  PracticeSlug,
  SingleFieldOutcomes,
  VIEW_TYPE,
  ValidAggregation,
} from "models";

import { Col, ExtraInfo, Flex, IconLinkButton, LoadingOverlay, Option, Select } from "components";

import { SubmissionSuccessModalContent } from "pages/app/all-field-view/components/SubmissionSuccessModal";
import { EvaluationMetricsPanel } from "pages/app/field-timeline/components/EvaluationMetricsPanel";
import { FieldTimelineResultCard } from "pages/app/field-timeline/components/FieldTimelineResultCard";
import { MissingDataGuidance } from "pages/app/field-timeline/components/MissingDataGuidance";
import { calculateImpactPanelBodyHeight } from "pages/app/field-timeline/utils/TimelineUtils";

type FieldTimelineImpactPanelProps = {
  carbonProgramOutcomes: CarbonProgramOutcomesResponse;
  progress: number;
  viewType: VIEW_TYPE;
  fieldSizes: Record<string, number>;
  reportingYearFromProps: number;
  hasMissingData?: boolean;
  setNavigateToMissingData?: (value: boolean) => void;
  practiceBundles?: PracticeBundle[];
  shouldShowSubmissionSuccessModalContent?: boolean;
  hidePayouts?: boolean;
};

// This function is used to filter carbon program outcomes to show only current and future years,
// excluding historical data that may not be relevant for current impact calculations.
export const filterFieldOutcomesByYear = (data: CarbonProgramOutcomesResponse, minYear: number = moment().year()) =>
  Object.entries(data).reduce((filteredData, [fieldId, field]) => {
    const singleField = field as SingleFieldOutcomes;
    if (!singleField?.fieldOutcomes) {
      return { ...filteredData, [fieldId]: singleField };
    }

    const filteredPractices = Object.entries(singleField.fieldOutcomes).reduce((practices, [practiceSlug, practice]) => {
      if (!practice || typeof practice !== "object" || !("years" in practice) || !practice.years) {
        return practices;
      }

      const validYears = Object.entries(practice.years)
        .filter(([year]) => Number(year) >= minYear)
        .reduce(
          (yearsAccumulator, [year, yearData]) => ({
            ...yearsAccumulator,
            [year]: yearData,
          }),
          {}
        );

      return {
        ...practices,
        [practiceSlug]: {
          ...practice,
          years: validYears,
        },
      };
    }, {});

    return {
      ...filteredData,
      [fieldId]: {
        ...singleField,
        fieldOutcomes: filteredPractices,
      },
    };
  }, {} as CarbonProgramOutcomesResponse);

export const InterventionImpactPanel = ({
  carbonProgramOutcomes,
  progress,
  fieldSizes,
  viewType,
  hasMissingData = false,
  setNavigateToMissingData,
  practiceBundles,
  hidePayouts,
  shouldShowSubmissionSuccessModalContent = false,
  reportingYearFromProps,
}: FieldTimelineImpactPanelProps) => {
  const { t } = useTranslation(["fieldTimeline"]);
  const env = getEnvironment();
  const areEvaluationMetricsEnabled = env.production ? checkFeatureFlag("evaluation-metrics") : true;
  const shouldHidePanel = !areEvaluationMetricsEnabled && hidePayouts;

  const [showPayoutDrawer, setShowPayoutDrawer] = useState(false);
  const [selectedPanel, setSelectedPanel] = useState<"payout" | EVALUATION_METRICS>(hidePayouts ? EVALUATION_METRICS.HUMUS : "payout");

  const [calculationType, setCalculationType] = useState<IMPACT_PANEL_CALCULATION_TYPE>(
    viewType === VIEW_TYPE.SIMULATION ? IMPACT_PANEL_CALCULATION_TYPE.PER_HA : IMPACT_PANEL_CALCULATION_TYPE.TOTAL
  );
  const { currentFarm } = useGetFarms();
  const { data: activeFarmReportingYear } = useGetFarmReportingYear(currentFarm?.id || null);
  const reportingYear = activeFarmReportingYear ?? reportingYearFromProps;

  const carbonProgramOutcomesUpFromReportingYear = filterFieldOutcomesByYear(carbonProgramOutcomes, reportingYear);
  const bodyHeight = calculateImpactPanelBodyHeight();

  const singleFieldOutcomes: SingleFieldOutcomes[] = Object.values(carbonProgramOutcomesUpFromReportingYear).filter(isSingleFieldOutcomes);

  // We only need to fetch past payments for the all fields view
  const isAllFieldsView = viewType === VIEW_TYPE.ALL_FIELDS;

  const { data: carbonProgram } = useGetCarbonProgramByFarmId(currentFarm?.id ?? undefined);
  const { data: farmPayments } = useGetFarmPayments(isAllFieldsView);
  const pastPayouts = isAllFieldsView ? groupPaymentsByYear(farmPayments?.payments.filter(({ year }) => year < reportingYear) ?? []) : [];

  const { years, allYearsLabel } = generateYearLabels(singleFieldOutcomes, pastPayouts);
  const [selectedYear, setSelectedYear] = useState<number | string>(allYearsLabel);

  const calculationTypeOptions = [
    { label: t("impactPanel.total"), value: IMPACT_PANEL_CALCULATION_TYPE.TOTAL },
    { label: t("impactPanel.perHa"), value: IMPACT_PANEL_CALCULATION_TYPE.PER_HA },
  ];

  const { fieldsContributingToPayouts, totalFields } = calculateFieldContributionStatus(carbonProgramOutcomes ?? {}, reportingYear);

  const fieldIds = Object.keys(carbonProgramOutcomes);
  const practiceSlugs = fieldIds
    .map((fieldId) => {
      const fieldCarbonProgramOutcomes = carbonProgramOutcomes[fieldId];
      if ("error" in fieldCarbonProgramOutcomes) return [];

      return Object.keys(fieldCarbonProgramOutcomes.fieldOutcomes);
    })
    .flat() as PracticeSlug[];
  const uniquePracticeSlugs = Array.from(new Set(practiceSlugs));

  // evaluation metrics other than payout are not supported atm for simulations
  const panelMetrics = viewType !== VIEW_TYPE.SIMULATION && areEvaluationMetricsEnabled ? ["payout", ...Object.values(EVALUATION_METRICS)] : ["payout"];

  const { data: existingCorrectionsByFieldId } = useGetFieldEvaluationMetricCorrections({ fieldIds: areEvaluationMetricsEnabled ? fieldIds : [] });

  useEffect(() => {
    setSelectedYear(allYearsLabel);
  }, [allYearsLabel]);

  const hasErrorData = useMemo(
    () => someOutcomeHasErrorOfType(carbonProgramOutcomes ?? {}, CARBON_RUNNER_ERROR_TYPE.FOOTPRINT_SERVICE),
    [carbonProgramOutcomes]
  );

  return !shouldHidePanel ? (
    <Flex className="field-timeline-impact-panel container" data-testid="field-timeline-impact-panel" vertical={true}>
      <div className="field-timeline-impact-panel body" style={{ height: `${bodyHeight}rem` }}>
        <Flex vertical={true}>
          <Flex align="center" className="margin-bottom-100">
            <>
              {/* @ts-expect-error need to change */}
              <Select
                className="panel-select"
                dataTestId="panel-select"
                name="panelSelect"
                popupMatchSelectWidth={false}
                onChange={setSelectedPanel}
                defaultValue={selectedPanel}
                value={selectedPanel}
              >
                {panelMetrics.map((metric, i) => (
                  <Option
                    data-testid={`panel-select-option-${i}`}
                    value={metric}
                    key={i}
                    className="panel-select-option"
                    disabled={metric === "payout" && hidePayouts}
                  >
                    {metric === "payout" ? t("impactPanel.payout") : t(`impactPanel.evaluationMetrics.${metric}`)}
                  </Option>
                ))}
              </Select>
              {selectedPanel === "payout" && (
                <IconLinkButton
                  dataTestId="field-timeline-payout-info-icon-button"
                  className="field-timeline-payout-info-icon-button"
                  theme="grey"
                  iconType="info"
                  size="small"
                  onClick={() => setShowPayoutDrawer(true)}
                />
              )}
            </>
          </Flex>
          {!!years.length && selectedPanel === "payout" && !hasErrorData && !hasMissingData && (
            <>
              <Flex justify="space-between" align="center">
                {/* @ts-expect-error need to change */}
                <Select
                  key={selectedYear}
                  className="payout-years-select"
                  dataTestId="payout-years-select"
                  name="payoutYears"
                  popupMatchSelectWidth={false}
                  onChange={(value) => {
                    setSelectedYear(value);
                    // if the selected year is before the reporting year, we set the view option to total since the per ha option is going to be disabled
                    if (Number(value) < reportingYear) setCalculationType(IMPACT_PANEL_CALCULATION_TYPE.TOTAL);
                  }}
                  defaultValue={selectedYear}
                  value={selectedYear}
                >
                  {[allYearsLabel, ...years].map((year, i) => (
                    <Option data-testid={`payout-year-select-option-${i}`} value={year} key={i} className="payout-years-select-option">
                      <Flex align="center">
                        {year}
                        {Number(year) === reportingYear ? (
                          <div data-testid="reporting-year-label" className="reporting-year-label">
                            {t("impactPanel.reportingYear")}
                          </div>
                        ) : null}
                      </Flex>
                    </Option>
                  ))}
                </Select>
                {/* Only show per ha option if current reporting year or future year is selected, because past payouts might have different farm sizes, which would lead to a lot more complexity in the code */}
                {viewType !== VIEW_TYPE.SIMULATION && (
                  //  @ts-expect-error need to change
                  <Select
                    // add the value as key to force a re-render when the value is programmatically updated by the year selection (per ha not allowed for past years)
                    key={calculationType}
                    className="field-timeline-result-select"
                    dataTestId="field-timeline-result-select"
                    name="resultSelect"
                    popupMatchSelectWidth={false}
                    onChange={setCalculationType}
                    defaultValue={calculationType}
                    value={calculationType}
                  >
                    {calculationTypeOptions.map((option, i) => (
                      <Option
                        data-testid={`field-timeline-result-select-option-${i}`}
                        value={option.value}
                        key={i}
                        disabled={option.value === IMPACT_PANEL_CALCULATION_TYPE.PER_HA && Number(selectedYear) < reportingYear}
                        className="field-timeline-result-select-option"
                      >
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                )}
              </Flex>
              <span className="dotted-line-divider" />
            </>
          )}
          {progress === 100 ? (
            <>
              {shouldShowSubmissionSuccessModalContent ? (
                <SubmissionSuccessModalContent
                  carbonProgramOutcomes={carbonProgramOutcomes ?? null}
                  farmId={currentFarm?.id ?? ""}
                  handleStartNextYear={() => {}} // 🚨 TODO : Follow up handle next year start
                  variant="panel"
                />
              ) : selectedPanel === "payout" ? (
                <FieldTimelineResultCard
                  resultType={selectedYear !== allYearsLabel ? IMPACT_PANEL_RESULT_TYPE.YEARLY : IMPACT_PANEL_RESULT_TYPE.ALL_YEARS}
                  calculationType={calculationType}
                  singleFieldOutcomes={singleFieldOutcomes}
                  showResults={!!years.length && !carbonProgram?.hidePayouts}
                  selectedYear={selectedYear === allYearsLabel ? selectedYear : Number(selectedYear)}
                  yearRange={years.map(Number)}
                  reportingYear={reportingYear}
                  practiceSlugs={uniquePracticeSlugs}
                  fieldSizes={fieldSizes}
                  viewType={viewType}
                  hasMissingData={hasMissingData}
                  hasErrorData={hasErrorData}
                  setNavigateToMissingData={setNavigateToMissingData}
                  practiceBundles={practiceBundles}
                  pastPayouts={pastPayouts}
                />
              ) : (
                <EvaluationMetricsPanel
                  selectedMetric={selectedPanel}
                  carbonProgramOutcomes={carbonProgramOutcomes}
                  reportingYear={reportingYear}
                  fieldSizes={fieldSizes}
                  viewType={viewType}
                  existingCorrectionsByFieldId={existingCorrectionsByFieldId ?? {}}
                  reportingPeriod={carbonProgram?.reportingPeriod}
                />
              )}
              {/* Do not show guidance for past years */}
              {isAllFieldsView &&
                !hasErrorData &&
                selectedPanel === "payout" &&
                !(Number(selectedYear) < reportingYear) &&
                !shouldShowSubmissionSuccessModalContent && (
                  <MissingDataGuidance fieldsContributingToPayouts={fieldsContributingToPayouts} totalFields={totalFields} />
                )}
            </>
          ) : (
            <Flex align="center" vertical>
              <LoadingOverlay>
                <Col className="loading-message">
                  <p>{t("impactPanel.fieldsCalculated")}</p>
                </Col>
              </LoadingOverlay>
            </Flex>
          )}
        </Flex>
      </div>
      <ExtraInfo setVisible={setShowPayoutDrawer} type="payout-overview" visible={showPayoutDrawer} />
    </Flex>
  ) : null;
};

const generateYearLabels = (singleFieldOutcomes: SingleFieldOutcomes[], pastPayouts: Payment[]) => {
  const fieldOutcomeYears = singleFieldOutcomes.flatMap(({ fieldOutcomes }) => {
    if (isEmptyObject(fieldOutcomes)) return [];

    const calculationsPerPractice = fieldOutcomes[PRACTICE_SLUG.CROPS];
    if (!calculationsPerPractice || isEmptyObject(calculationsPerPractice)) return [];

    const yearsCalculations = calculationsPerPractice.years;
    if (isEmptyObject(yearsCalculations)) return [];

    return Object.keys(yearsCalculations).filter((year) => {
      const yearlyOutcomesPerPractice = yearsCalculations[year];
      return (
        yearlyOutcomesPerPractice &&
        !yearlyOutcomesPerPractice.isBaselineYear &&
        yearlyOutcomesPerPractice.validation?.valid &&
        typeof (yearlyOutcomesPerPractice as ValidAggregation)?.payouts?.avg === "number"
      );
    });
  });

  const pastYears = pastPayouts.map(({ year }) => year);

  const years = Array.from(new Set([...pastYears, ...fieldOutcomeYears])).sort();

  const allYearsLabel = years.length ? `${years[0]} - ${years[years.length - 1]}` : "";
  return { years, allYearsLabel };
};

const groupPaymentsByYear = (payments: Payment[]) =>
  payments.reduce((acc, payment) => {
    const existingPayment = acc.find((p) => p.year === payment.year);
    if (existingPayment) {
      existingPayment.amount += payment.amount;
    } else {
      acc.push({ ...payment });
    }
    return acc;
  }, [] as Payment[]);
